/**
 * useDeleteApplication Hook
 *
 * Custom React Query mutation hook for deleting applications.
 * Supports two-step 2FA verification flow.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { applicationService } from '../../../api/application.service';
import { getErrorMessage } from '../../../lib/utils/errors';
import type { TwoFactorRequiredResponse } from '../../../types/auth.types';

interface DeleteApplicationParams {
  applicationId: string;
  verificationCodeId?: string;
  verificationCode?: string;
}

interface UseDeleteApplicationOptions {
  onSuccess?: () => void;
  onError?: (error: string) => void;
  onTwoFactorRequired?: (response: TwoFactorRequiredResponse) => void;
}

/**
 * Hook for deleting an application with 2FA verification
 * @param tenantId - Tenant ID
 * @param options - Success/error/2FA callbacks
 */
export const useDeleteApplication = (tenantId: string, options?: UseDeleteApplicationOptions) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: DeleteApplicationParams) =>
      applicationService.deleteApplication(
        tenantId,
        params.applicationId,
        params.verificationCodeId,
        params.verificationCode
      ),
    onSuccess: (data) => {
      // Check if 2FA is required
      if ('twoFactorRequired' in data && data.twoFactorRequired) {
        options?.onTwoFactorRequired?.(data);
        return;
      }

      // Actual deletion succeeded
      queryClient.invalidateQueries({ queryKey: ['applications', tenantId] });
      options?.onSuccess?.();
    },
    onError: (error: unknown) => {
      const errorMessage = getErrorMessage(error);
      options?.onError?.(errorMessage);
    },
  });
};
